import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Link2,
  Bell,
  Moon,
  Palette,
  Globe,
  CheckCircle,
  Bot,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { HandoffAlertsCard, TestThreadCard } from "../components/AutoResponderExtras";

const SettingsPage = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState({
    email: true,
    browser: false,
    ticketUpdates: true,
    teamActivity: false
  });
  
  // Auto-responder setting
  const [autoResponderEnabled, setAutoResponderEnabled] = useState(true);
  const [autoResponderLoading, setAutoResponderLoading] = useState(true);
  const [autoResponderSaving, setAutoResponderSaving] = useState(false);
  
  // Follow-up question setting
  const [followupEnabled, setFollowupEnabled] = useState(true);
  const [followupSaving, setFollowupSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [responderRes, followupRes] = await Promise.all([
        axios.get(`${API}/settings/auto-responder`, { withCredentials: true }),
        axios.get(`${API}/settings/auto-responder/followup`, { withCredentials: true })
      ]);
      setAutoResponderEnabled(responderRes.data.enabled);
      setFollowupEnabled(followupRes.data.enabled);
    } catch (error) {
      toast.error("Failed to load auto-responder settings");
    } finally {
      setAutoResponderLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleAutoResponderChange = async (enabled) => {
    setAutoResponderSaving(true);
    try {
      await axios.put(`${API}/settings/auto-responder`, { enabled }, { withCredentials: true });
      setAutoResponderEnabled(enabled);
      toast.success(enabled ? "Auto-responder enabled" : "Auto-responder disabled");
    } catch (error) {
      toast.error("Failed to update setting");
    } finally {
      setAutoResponderSaving(false);
    }
  };
  
  const handleFollowupChange = async (enabled) => {
    setFollowupSaving(true);
    try {
      await axios.put(`${API}/settings/auto-responder/followup`, { enabled }, { withCredentials: true });
      setFollowupEnabled(enabled);
      toast.success(enabled ? "Follow-up questions enabled" : "Follow-up questions disabled");
    } catch (error) {
      toast.error("Failed to update setting");
    } finally {
      setFollowupSaving(false);
    }
  };

  const handleNotificationChange = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
    toast.success("Settings updated");
  };

  return (
    <Layout title="Settings">
      <div className="space-y-8 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-indigo-500" />
                Integrations
              </CardTitle>
              <CardDescription className="text-slate-500">Manage your connected services and APIs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <path d="M17.5 8.5V5.5C17.5 4.395 16.605 3.5 15.5 3.5C14.395 3.5 13.5 4.395 13.5 5.5V8.5" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="15.5" cy="11.5" r="3" stroke="#f97316" strokeWidth="1.5" />
                      <path d="M12.5 11.5H8.5C7.395 11.5 6.5 12.395 6.5 13.5V16.5C6.5 17.605 7.395 18.5 8.5 18.5H12.5" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="5" cy="8" r="1.5" stroke="#f97316" strokeWidth="1.5" />
                      <path d="M5 9.5V11.5" stroke="#f97316" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">HubSpot</p>
                    <p className="text-sm text-slate-500">Ticket management & CRM</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  Connected
                </span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 17L12 22L22 17" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 12L12 17L22 12" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Claude AI (Sonnet)</p>
                    <p className="text-sm text-slate-500">AI-powered response drafting</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  Connected
                </span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Google Authentication</p>
                    <p className="text-sm text-slate-500">Team sign-in via Gmail</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  Active
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Bot className="w-5 h-5 text-indigo-500" />
                Live Chat Auto-Responder
              </CardTitle>
              <CardDescription className="text-slate-500">Control automatic AI responses on HubSpot Live Chat</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex-1">
                  <Label className="text-slate-900 font-medium">Auto-Responder</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    When enabled, AI automatically responds to customer messages on TalentDesk Live Chat
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {(autoResponderLoading || autoResponderSaving) && (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  )}
                  <Switch 
                    checked={autoResponderEnabled} 
                    onCheckedChange={handleAutoResponderChange}
                    disabled={autoResponderLoading || autoResponderSaving}
                    data-testid="auto-responder-switch"
                  />
                </div>
              </div>
              
              {/* Follow-up Questions Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex-1">
                  <Label className="text-slate-900 font-medium">Follow-up Questions</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    After AI responds, ask if the answer was helpful with 3 options
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {followupSaving && (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  )}
                  <Switch 
                    checked={followupEnabled} 
                    onCheckedChange={handleFollowupChange}
                    disabled={autoResponderLoading || followupSaving || !autoResponderEnabled}
                    data-testid="followup-switch"
                  />
                </div>
              </div>
              
              <p className="text-xs text-slate-400">
                {autoResponderEnabled 
                  ? "✅ AI is responding to customer messages automatically" 
                  : "⏸️ AI responses are paused - messages will be logged but not answered"}
              </p>
              {autoResponderEnabled && followupEnabled && (
                <p className="text-xs text-slate-400">
                  📋 Follow-up: 1) Yes, answered 2) Need more help 3) Talk to a person
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}>
          <HandoffAlertsCard />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
          <TestThreadCard />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-indigo-500" />
                Notifications
              </CardTitle>
              <CardDescription className="text-slate-500">Configure how you receive alerts and updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-900 font-medium">Email Notifications</Label>
                  <p className="text-sm text-slate-500">Receive updates via email</p>
                </div>
                <Switch checked={notifications.email} onCheckedChange={() => handleNotificationChange("email")} data-testid="email-notifications-switch" />
              </div>
              <Separator className="bg-slate-100" />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-900 font-medium">Browser Notifications</Label>
                  <p className="text-sm text-slate-500">Get push notifications in browser</p>
                </div>
                <Switch checked={notifications.browser} onCheckedChange={() => handleNotificationChange("browser")} data-testid="browser-notifications-switch" />
              </div>
              <Separator className="bg-slate-100" />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-900 font-medium">Ticket Updates</Label>
                  <p className="text-sm text-slate-500">Alerts for new and updated tickets</p>
                </div>
                <Switch checked={notifications.ticketUpdates} onCheckedChange={() => handleNotificationChange("ticketUpdates")} data-testid="ticket-updates-switch" />
              </div>
              <Separator className="bg-slate-100" />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-900 font-medium">Team Activity</Label>
                  <p className="text-sm text-slate-500">Updates when team members join or leave</p>
                </div>
                <Switch checked={notifications.teamActivity} onCheckedChange={() => handleNotificationChange("teamActivity")} data-testid="team-activity-switch" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-500" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Name</span>
                  <span className="text-slate-900 font-medium">{user?.name}</span>
                </div>
                <Separator className="bg-slate-100" />
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Email</span>
                  <span className="text-slate-900 font-medium">{user?.email}</span>
                </div>
                <Separator className="bg-slate-100" />
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Role</span>
                  <span className="text-slate-900 font-medium capitalize">{user?.role}</span>
                </div>
                <Separator className="bg-slate-100" />
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">User ID</span>
                  <span className="mono text-sm text-slate-400">{user?.user_id}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
